import { FormArray, FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { NumberFormatDialogComponent } from '../ng-table-grid/number-format-dialog.component';
import { UnitsGridFormattingHelper } from '../../helpers/units-grid-formatting.helper';
import {
  applyUnitsGridPasteMatrix,
  isMatrixPasteText
} from './units-grid-input-paste.helper';
import { normalizeUnitsGridRowsByFirstNumericColumn } from './units-grid-input-paste-normalization.helper';
import {
  createUnitsGridFileInput,
  readUnitsGridImportText,
  resolveUnitsGridPasteFormat
} from './units-grid-input-interactions.helper';
import { isUnitsGridEditable, isUnitsGridNumericColumn } from './units-grid-input-display.helper';
import { ensureUnitsGridRow } from './units-grid-input-row-ops.helper';
import {
  UnitsGridColumn,
  UnitsGridPasteAppliedEvent,
  UnitsGridPasteRange,
  UnitsGridPasteTransform,
  UnitsGridRowFactory
} from './units-grid-input.models';

export interface UnitsGridClipboardCoordinatorConfig {
  enableClipboardPaste: () => boolean;
  enableFileImport: () => boolean;
  enableDelimiterDialog: () => boolean;
  getColumns: () => readonly UnitsGridColumn[];
  getBeforePasteTransform: () => UnitsGridPasteTransform | null;
  getNormalizePasteByFirstColumn: () => boolean;
  getDedupePasteByFirstColumn: () => boolean;
  getSortPasteByFirstColumn: () => boolean;
  getNumberFormatDialogData: () => Readonly<Record<string, unknown>>;
  getRows: () => FormArray<FormGroup>;
  getCreateRow: () => UnitsGridRowFactory | null;
  onPasteApplying: (range: UnitsGridPasteRange) => void;
  onPasteApplied: (event: UnitsGridPasteAppliedEvent) => void;
  requestCommitAfterPaste: () => void;
}

export class UnitsGridClipboardCoordinator {
  private fileInputElement: HTMLInputElement | null = null;
  private numberFormatDialogRef: DynamicDialogRef | null = null;
  private currentThousandsDelimiter = ',';
  private currentDecimalDelimiter = '.';
  private currentPasteDialogResult: Readonly<Record<string, unknown>> | undefined;

  constructor(
    private readonly dialogService: DialogService,
    private readonly config: UnitsGridClipboardCoordinatorConfig
  ) {}

  public destroy(): void {
    this.numberFormatDialogRef?.close();
    this.numberFormatDialogRef = null;
  }

  public onCellPaste(event: ClipboardEvent, rowIndex: number, columnIndex: number): void {
    if (!this.config.enableClipboardPaste()) {
      return;
    }

    const pastedText = event.clipboardData?.getData('text') ?? '';
    if (!isMatrixPasteText(pastedText)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    void this.applyClipboardText(pastedText, rowIndex, columnIndex);
  }

  public triggerImportFilePicker(): void {
    if (!this.config.enableFileImport()) {
      return;
    }

    if (!this.fileInputElement) {
      this.fileInputElement = createUnitsGridFileInput(async (event) => {
        await this.onImportFileSelected(event);
      });
    }

    this.fileInputElement.value = '';
    this.fileInputElement.click();
  }

  public async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    const normalizedContent = await readUnitsGridImportText(file);
    await this.applyClipboardText(normalizedContent, 0, 0);
  }

  public async pasteFromClipboard(startRowIndex: number, startColumnIndex: number): Promise<void> {
    if (!this.config.enableClipboardPaste()) {
      return;
    }

    const text = await navigator.clipboard.readText();
    await this.applyClipboardText(text, startRowIndex, startColumnIndex);
  }

  public async applyClipboardText(text: string, startRowIndex: number, startColumnIndex: number): Promise<void> {
    const processedText = await this.normalizeNumberFormatForPaste(text);
    if (processedText === null) {
      return;
    }

    const normalizedText = UnitsGridFormattingHelper.normalizeImportedText(processedText);
    const parsedRows = UnitsGridFormattingHelper.parseClipboardRows(normalizedText);
    if (parsedRows.length === 0) {
      return;
    }

    const columns = this.config.getColumns();
    const normalizedRows = this.config.getNormalizePasteByFirstColumn()
      ? normalizeUnitsGridRowsByFirstNumericColumn(parsedRows, {
          dedupeByFirstColumn: this.config.getDedupePasteByFirstColumn(),
          sortByFirstColumn: this.config.getSortPasteByFirstColumn(),
          dedupePrecision: 6
        })
      : parsedRows;

    const transform = this.config.getBeforePasteTransform();
    const transformedRows = transform
      ? transform({
          startRowIndex,
          startColumnIndex,
          columns,
          parsedRows: normalizedRows,
          pasteDialogResult: this.currentPasteDialogResult
        })
      : normalizedRows;

    const rows = this.config.getRows();
    const pasteRange: UnitsGridPasteRange = {
      startRowIndex,
      startColumnIndex,
      rowCount: transformedRows.length,
      columnCount: Math.max(0, ...transformedRows.map((row) => row.length))
    };
    this.config.onPasteApplying(pasteRange);

    const changedCells = applyUnitsGridPasteMatrix({
      matrix: transformedRows,
      startRowIndex,
      startColumnIndex,
      columns,
      ensureRow: (rowIndex) => ensureUnitsGridRow(rows, this.config.getCreateRow(), rowIndex),
      isEditable: (column) => isUnitsGridEditable(column),
      normalizeValue: (value, column) => this.normalizePastedValue(value, column)
    });

    if (changedCells > 0) {
      this.config.onPasteApplied({ ...pasteRange, changedCells });
      this.config.requestCommitAfterPaste();
    }
  }

  private async normalizeNumberFormatForPaste(text: string): Promise<string | null> {
    const result = await resolveUnitsGridPasteFormat({
      text,
      hasNumericColumns: this.hasAnyNumericColumn(),
      enableDelimiterDialog: this.config.enableDelimiterDialog(),
      currentThousandsDelimiter: this.currentThousandsDelimiter,
      currentDecimalDelimiter: this.currentDecimalDelimiter,
      dialogData: this.config.getNumberFormatDialogData(),
      openNumberFormatDialog: async () => {
        const dialogRef = this.dialogService.open(NumberFormatDialogComponent, {
          header: 'Number Format',
          closable: false,
          data: this.config.getNumberFormatDialogData()
        });

        if (!dialogRef) {
          return null;
        }

        this.numberFormatDialogRef = dialogRef;
        const dialogResult = await firstValueFrom<{ thousands: string; decimal: string } | null>(dialogRef.onClose);
        this.numberFormatDialogRef = null;

        return dialogResult;
      }
    });

    if (!result) {
      return null;
    }

    this.currentThousandsDelimiter = result.thousands;
    this.currentDecimalDelimiter = result.decimal;
    this.currentPasteDialogResult = result.dialogResult;
    return result.text;
  }

  private normalizePastedValue(value: string, column: UnitsGridColumn): string | number | null | undefined {
    if (!isUnitsGridNumericColumn(column)) {
      return value;
    }

    return UnitsGridFormattingHelper.parseNumericCellValue(
      value,
      this.currentThousandsDelimiter,
      this.currentDecimalDelimiter
    );
  }

  private hasAnyNumericColumn(): boolean {
    return this.config.getColumns().some((column) => isUnitsGridNumericColumn(column));
  }
}
